import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * PostOnboardingLeadCapture
 * Warm amber/purple premium popup — dashboard stays fully visible behind it.
 */

const AMBER = "#FF8A3D";
const AMBER_SOFT = "rgba(255,138,61,0.55)";
const PINK = "#FF5D8F";
const PURPLE = "rgba(168,85,247,0.55)";

const STORAGE_KEY = "orbit_postonb_lead_seen";

const normalizeUrl = (raw: string) => {
  const t = raw.trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
};
const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

type FeatureProps = {
  color: string;
  title: string;
  desc: string;
  icon: React.ReactNode;
};

const Feature = ({ color, title, desc, icon }: FeatureProps) => (
  <div className="flex flex-col items-center text-center">
    <div
      className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl"
      style={{
        background: `linear-gradient(180deg, ${color}26 0%, ${color}10 100%)`,
        border: `1px solid ${color}40`,
        boxShadow: `0 0 18px ${color}33, inset 0 1px 0 rgba(255,255,255,0.06)`,
        color,
      }}
    >
      {icon}
    </div>
    <div className="text-[12px] font-semibold leading-tight" style={{ color }}>
      {title}
    </div>
    <div className="mt-1 text-[10.5px] leading-snug text-white/55">{desc}</div>
  </div>
);

const PostOnboardingLeadCapture = () => {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [viewport, setViewport] = useState({ height: 0, offsetTop: 0 });

  const [url, setUrl] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Cinematic finale shown after the "demo is on the way" sequence.
  const [finale, setFinale] = useState(false);
  const [finaleClosing, setFinaleClosing] = useState(false);

  const closeTimer = useRef<number | null>(null);
  const urlRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const handler = () => {
      if (dismissed) return;
      if (sessionStorage.getItem(STORAGE_KEY) === "1") return;
      window.setTimeout(() => setOpen(true), 1400);
    };
    window.addEventListener("orbit:onboarding-finished", handler);
    return () => window.removeEventListener("orbit:onboarding-finished", handler);
  }, [dismissed]);

  useEffect(() => {
    if (!open) return;
    document.body.classList.add("orbit-lead-capture-open");
    const updateViewport = () => {
      const vv = window.visualViewport;
      setViewport({
        height: vv?.height ?? window.innerHeight,
        offsetTop: vv?.offsetTop ?? 0,
      });
    };
    updateViewport();
    window.visualViewport?.addEventListener("resize", updateViewport);
    window.visualViewport?.addEventListener("scroll", updateViewport);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close(true);
    };
    window.addEventListener("keydown", onKeyDown);
    const canAutoFocus = window.matchMedia("(min-width: 640px)").matches;
    const t = window.setTimeout(() => {
      if (canAutoFocus) urlRef.current?.focus();
    }, 280);
    return () => {
      window.clearTimeout(t);
      window.visualViewport?.removeEventListener("resize", updateViewport);
      window.visualViewport?.removeEventListener("scroll", updateViewport);
      window.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("orbit-lead-capture-open");
    };
  }, [open]);

  useEffect(() => {
    if (open) return;
    document.body.classList.remove("orbit-lead-capture-open");
    return () => document.body.classList.remove("orbit-lead-capture-open");
  }, [open]);

  const close = (markSeen = true) => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setClosing(true);
    closeTimer.current = window.setTimeout(() => {
      setOpen(false);
      setClosing(false);
      closeTimer.current = null;
      if (markSeen) {
        sessionStorage.setItem(STORAGE_KEY, "1");
        setDismissed(true);
      }
    }, 260);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const normalized = normalizeUrl(url);
    if (!normalized) {
      setError("Please enter your website URL.");
      return;
    }
    if (!isValidEmail(email)) {
      setError("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      try {
        localStorage.setItem("orbit_pending_website_url", normalized);
        localStorage.setItem("orbit_pending_email", email.trim());
      } catch {}
      try {
        await supabase.functions.invoke("notify-demo-activated", {
          body: { email: email.trim(), website_url: normalized, source: 'post_onboarding_popup' },
        });
      } catch (err) {
        console.warn("[lead-capture] notify-demo-activated failed", err);
      }
      setSuccess(true);
      sessionStorage.setItem(STORAGE_KEY, "1");
      // Keep the "Your demo is on the way" card visible ~6.5s, then hand off
      // gracefully to the cinematic finale overlay.
      window.setTimeout(() => {
        try {
          document.body.classList.add("orbit-calm");
          window.dispatchEvent(new CustomEvent("orbit:preview-finale"));
        } catch {}
        close(true);
        window.setTimeout(() => setFinale(true), 280);
      }, 6500);
    } catch (err) {
      console.error("[lead-capture] submit error", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const closeFinale = () => {
    setFinaleClosing(true);
    window.setTimeout(() => {
      try {
        document.body.classList.remove("orbit-calm");
      } catch {}
      setFinale(false);
      setFinaleClosing(false);
    }, 420);
  };

  // Cinematic finale overlay — soft glass, dashboard still visible behind.
  if (finale) {
    return (
      <div
        className="fixed inset-0 z-[360] flex items-center justify-center px-4"
        style={{
          opacity: finaleClosing ? 0 : 1,
          transition: "opacity 420ms ease",
          background:
            "radial-gradient(ellipse at center, rgba(10,8,18,0.55) 0%, rgba(6,5,12,0.75) 60%, rgba(4,3,10,0.85) 100%)",
          backdropFilter: "blur(6px) saturate(115%)",
          WebkitBackdropFilter: "blur(6px) saturate(115%)",
          animation: "finaleVeilIn 720ms ease-out both",
        }}
        role="dialog"
        aria-modal="true"
        aria-label="OrbitRank preview closing"
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 50% 40%, transparent 0%, transparent 35%, rgba(0,0,0,0.35) 100%)",
          }}
        />
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(255,138,61,0.18) 0%, rgba(168,85,247,0.12) 45%, transparent 70%)",
            filter: "blur(40px)",
            animation: "finaleBreath 6s ease-in-out infinite",
          }}
        />

        <div
          className="relative w-full overflow-hidden rounded-[26px] text-center"
          style={{
            maxWidth: 480,
            padding: "44px 32px 32px",
            background:
              "linear-gradient(180deg, rgba(22,20,30,0.62) 0%, rgba(14,12,22,0.66) 100%)",
            border: "1px solid rgba(255,255,255,0.10)",
            backdropFilter: "blur(28px) saturate(140%)",
            WebkitBackdropFilter: "blur(28px) saturate(140%)",
            boxShadow:
              "0 1px 0 rgba(255,255,255,0.08) inset, 0 30px 90px rgba(0,0,0,0.55), 0 0 80px rgba(255,138,61,0.14), 0 0 140px rgba(168,85,247,0.10)",
            animation: "finaleCardIn 820ms cubic-bezier(.2,.7,.2,1) both",
          }}
        >
          <div
            className="pointer-events-none absolute left-0 right-0 top-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(255,138,61,0.55), rgba(168,85,247,0.55), transparent)",
            }}
          />

          <div
            className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl"
            style={{
              background:
                "linear-gradient(180deg, rgba(255,138,61,0.18) 0%, rgba(168,85,247,0.14) 100%)",
              border: "1px solid rgba(255,138,61,0.35)",
              boxShadow:
                "0 0 28px rgba(255,138,61,0.30), inset 0 1px 0 rgba(255,255,255,0.08)",
              color: AMBER,
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect width="20" height="16" x="2" y="4" rx="2" />
              <path d="m22 7-10 5L2 7" />
            </svg>
          </div>

          <h2 className="text-[24px] sm:text-[28px] font-semibold leading-[1.15] tracking-[-0.02em] text-white">
            We've sent the next step to{" "}
            <span
              style={{
                background: `linear-gradient(90deg, ${AMBER} 0%, ${PINK} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              your email
            </span>
            .
          </h2>

          <p className="mx-auto mt-3 max-w-[340px] text-[13px] leading-relaxed text-white/55">
            Your personalised OrbitRank workspace is being prepared. We'll be in
            touch shortly.
          </p>

          <button
            type="button"
            onClick={closeFinale}
            className="relative mt-7 inline-flex h-[52px] w-full items-center justify-center overflow-hidden rounded-[14px] text-[15px] font-semibold text-white transition-transform"
            style={{
              background: `linear-gradient(90deg, ${AMBER} 0%, ${PINK} 100%)`,
              boxShadow:
                "0 10px 30px rgba(255,93,143,0.40), 0 0 40px rgba(255,138,61,0.35), inset 0 1px 0 rgba(255,255,255,0.30)",
              letterSpacing: "-0.01em",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.filter = "brightness(1.06)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.filter = "brightness(1)";
            }}
          >
            See you soon
          </button>

          <button
            type="button"
            onClick={() => {
              closeFinale();
              window.setTimeout(() => {
                try {
                  window.location.assign("/");
                } catch {}
              }, 280);
            }}
            className="mt-4 text-[12.5px] font-medium text-white/50 transition-colors hover:text-white/80"
          >
            Back to main page
          </button>
        </div>

        <style>{`
          @keyframes finaleVeilIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
          @keyframes finaleCardIn {
            0% { opacity: 0; transform: translateY(14px) scale(0.97); filter: blur(4px); }
            100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
          }
          @keyframes finaleBreath {
            0%, 100% { opacity: 0.85; transform: translate(-50%, -50%) scale(1); }
            50% { opacity: 1; transform: translate(-50%, -50%) scale(1.06); }
          }
          body.orbit-calm #root > *:not([role="dialog"]) {
            filter: saturate(0.78) brightness(0.86);
            transition: filter 1400ms ease;
          }
          body.orbit-calm .animate-pulse,
          body.orbit-calm .animate-ping {
            animation-duration: 4s !important;
            opacity: 0.7;
            transition: opacity 1200ms ease;
          }
        `}</style>
      </div>
    );
  }

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-x-0 z-[10020] flex items-start justify-center overflow-x-hidden overflow-y-auto overscroll-contain px-3 sm:items-center sm:px-4"
        style={{
          top: viewport.offsetTop || 0,
          height: viewport.height ? `${viewport.height}px` : "100dvh",
          opacity: closing ? 0 : 1,
          transition: "opacity 240ms ease",
          paddingBottom: "max(96px, env(safe-area-inset-bottom))",
          paddingTop: "max(14px, env(safe-area-inset-top))",
          pointerEvents: "none",
          WebkitOverflowScrolling: "touch",
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Generate your personalized OrbitRank demo"
      >
        {/* Soft transparent dismiss layer — NO blur, NO darkening */}
        <div
          className="absolute inset-0"
          style={{ pointerEvents: "auto" }}
          onClick={() => close(true)}
        />

        <button
          type="button"
          onClick={() => close(true)}
          aria-label="Close popup"
          className="absolute right-4 z-30 flex h-11 w-11 items-center justify-center rounded-full text-white/80 transition-colors hover:text-white"
          style={{
            top: "max(12px, env(safe-area-inset-top))",
            pointerEvents: "auto",
            background: "rgba(8,8,12,0.92)",
            border: "1px solid rgba(255,255,255,0.16)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>

        <div
          onClick={(e) => e.stopPropagation()}
          className="orbit-mobile-scroll relative my-auto w-full overflow-x-hidden overflow-y-auto rounded-[20px] sm:rounded-[24px]"
          style={{
            maxWidth: 620,
            maxHeight: viewport.height
              ? `${Math.max(320, viewport.height - 118)}px`
              : "calc(100dvh - max(120px, env(safe-area-inset-bottom)))",
            pointerEvents: "auto",
            background:
              "linear-gradient(180deg, rgba(12,12,18,0.995) 0%, rgba(7,7,12,0.995) 100%)",
            border: `1.5px solid ${AMBER_SOFT}`,
            boxShadow: `
              0 0 0 1px rgba(255,138,61,0.18),
              0 30px 80px rgba(0,0,0,0.55),
              0 0 70px rgba(255,138,61,0.28),
              0 0 120px rgba(168,85,247,0.18)
            `,
            animation: "leadModalIn 460ms cubic-bezier(.2,.7,.2,1) both",
          }}
        >
          {/* Warm amber glow top-left */}
          <div
            className="pointer-events-none absolute -top-32 -left-24 h-72 w-72 rounded-full"
            style={{
              background: `radial-gradient(circle, rgba(255,138,61,0.45) 0%, transparent 65%)`,
              filter: "blur(30px)",
            }}
          />
          {/* Purple glow bottom-right */}
          <div
            className="pointer-events-none absolute -bottom-28 -right-20 h-72 w-72 rounded-full"
            style={{
              background: `radial-gradient(circle, ${PURPLE} 0%, transparent 65%)`,
              filter: "blur(30px)",
            }}
          />
          {/* Pink glow center-right */}
          <div
            className="pointer-events-none absolute top-1/3 -right-16 h-56 w-56 rounded-full opacity-60"
            style={{
              background: `radial-gradient(circle, rgba(255,93,143,0.35) 0%, transparent 65%)`,
              filter: "blur(28px)",
            }}
          />

          {/* Close button */}
          <button
            type="button"
            onClick={() => close(true)}
            aria-label="Close"
            className="absolute right-4 top-4 z-10 hidden h-8 w-8 items-center justify-center rounded-full text-white/70 transition-colors hover:text-white sm:flex"
            style={{
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.10)",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>

          <div className="relative px-5 pt-8 pb-6 sm:px-9 sm:pt-10 sm:pb-8">
            {!success ? (
              <>
                <h2
                  className="text-[28px] sm:text-[36px] font-bold leading-[1.05] tracking-[-0.02em] text-white"
                  style={{ letterSpacing: "-0.02em" }}
                >
                  See OrbitRank work for{" "}
                  <span
                    style={{
                      background: `linear-gradient(90deg, ${AMBER} 0%, ${PINK} 100%)`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    your business
                  </span>{" "}
                  <span
                    style={{
                      background: `linear-gradient(90deg, ${PINK} 0%, ${AMBER} 100%)`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    live.
                  </span>
                </h2>
                <p className="mt-3 text-[13px] sm:text-[14px] leading-relaxed text-white/65 max-w-[480px]">
                  Enter your website and email to generate your personalized
                  OrbitRank demo experience.
                </p>

                {/* Feature grid */}
                <div className="mt-5 grid grid-cols-2 gap-3 sm:mt-6 sm:grid-cols-4 sm:gap-4">
                  <Feature
                    color={AMBER}
                    title="AI-Powered Analysis"
                    desc="We analyze your entire online presence"
                    icon={
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" />
                      </svg>
                    }
                  />
                  <Feature
                    color="#C77DFF"
                    title="Actionable Insights"
                    desc="See exactly what's holding you back"
                    icon={
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="9" />
                        <circle cx="12" cy="12" r="5" />
                        <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                      </svg>
                    }
                  />
                  <Feature
                    color={PINK}
                    title="Custom Roadmap"
                    desc="Get your step-by-step growth plan"
                    icon={
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 17 9 11l4 4 8-8" />
                        <path d="M14 7h7v7" />
                      </svg>
                    }
                  />
                  <Feature
                    color="#A78BFA"
                    title="No Credit Card"
                    desc="Completely free. No strings attached."
                    icon={
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      </svg>
                    }
                  />
                </div>

                <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Website */}
                    <label
                      className="relative flex items-center gap-3 rounded-[14px] px-4 py-3 transition-colors"
                      style={{
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M2 12h20" />
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                      </svg>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="text-[10.5px] font-medium text-white/55">
                          Your Website
                        </span>
                        <input
                          ref={urlRef}
                          type="text"
                          inputMode="url"
                          autoComplete="url"
                          placeholder="e.g. yourbusiness.com"
                          value={url}
                          onChange={(e) => setUrl(e.target.value)}
                          required
                          maxLength={200}
                          className="w-full bg-transparent text-[13.5px] text-white outline-none placeholder:text-white/30"
                        />
                      </div>
                    </label>

                    {/* Email */}
                    <label
                      className="relative flex items-center gap-3 rounded-[14px] px-4 py-3 transition-colors"
                      style={{
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                        <rect width="20" height="16" x="2" y="4" rx="2" />
                        <path d="m22 7-10 5L2 7" />
                      </svg>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="text-[10.5px] font-medium text-white/55">
                          Your Email Address
                        </span>
                        <input
                          type="email"
                          inputMode="email"
                          autoComplete="email"
                          placeholder="you@yourbusiness.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          maxLength={200}
                          className="w-full bg-transparent text-[13.5px] text-white outline-none placeholder:text-white/30"
                        />
                      </div>
                    </label>
                  </div>

                  {error && (
                    <div
                      className="rounded-[10px] px-3 py-2 text-[12px]"
                      style={{
                        background: "rgba(239,68,68,0.08)",
                        border: "1px solid rgba(239,68,68,0.25)",
                        color: "#fecaca",
                      }}
                    >
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="relative mt-1 flex h-[58px] items-center justify-center gap-2.5 overflow-hidden rounded-[14px] text-[16px] font-semibold text-white transition-transform disabled:opacity-80"
                    style={{
                      background: `linear-gradient(90deg, #FF8A3D 0%, #FF5D8F 100%)`,
                      boxShadow: `
                        0 10px 30px rgba(255,93,143,0.45),
                        0 0 40px rgba(255,138,61,0.45),
                        inset 0 1px 0 rgba(255,255,255,0.35)
                      `,
                      letterSpacing: "-0.01em",
                    }}
                    onMouseEnter={(e) => {
                      if (submitting) return;
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.filter = "brightness(1.06)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.filter = "brightness(1)";
                    }}
                  >
                    {submitting ? (
                      <>
                        <span
                          className="inline-block h-4 w-4 rounded-full border-2 border-white/80 border-r-transparent"
                          style={{ animation: "leadSpin 0.7s linear infinite" }}
                        />
                        Generating your demo…
                      </>
                    ) : (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
                          <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
                          <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
                          <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
                        </svg>
                        Generate My Live Demo
                      </>
                    )}
                  </button>

                  <div className="mt-1.5 flex items-center justify-center gap-2 text-[11.5px] text-white/55">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                    100% Secure. We respect your privacy.
                  </div>

                  <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[10.5px] text-white/45">
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className="h-1 w-1 rounded-full"
                        style={{ background: AMBER, boxShadow: `0 0 6px ${AMBER}` }}
                      />
                      Real AI analysis
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className="h-1 w-1 rounded-full"
                        style={{ background: PINK, boxShadow: `0 0 6px ${PINK}` }}
                      />
                      Live dashboard preview
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className="h-1 w-1 rounded-full"
                        style={{ background: "#A78BFA", boxShadow: "0 0 6px #A78BFA" }}
                      />
                      Personalized SEO insights
                    </span>
                  </div>
                </form>
              </>
            ) : (
              <div style={{ animation: "leadStageIn 360ms ease-out both" }}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="relative flex h-2.5 w-2.5">
                    <span
                      className="absolute inline-flex h-full w-full rounded-full"
                      style={{
                        background: AMBER,
                        opacity: 0.55,
                        animation: "leadChipPing 2s cubic-bezier(0,0,0.2,1) infinite",
                      }}
                    />
                    <span
                      className="relative inline-flex h-2.5 w-2.5 rounded-full"
                      style={{ background: AMBER, boxShadow: `0 0 10px ${AMBER}` }}
                    />
                  </span>
                  <span
                    className="text-[10.5px] font-semibold uppercase tracking-[0.16em]"
                    style={{ color: AMBER }}
                  >
                    Preparing your workspace
                  </span>
                </div>
                <h2 className="text-[26px] sm:text-[30px] font-bold leading-tight tracking-[-0.02em] text-white">
                  Your demo is on the way.
                </h2>
                <p className="mt-3 text-[14px] leading-relaxed text-white/70">
                  We're spinning up a personalised OrbitRank workspace for{" "}
                  <span style={{ color: AMBER }}>{normalizeUrl(url)}</span>. Watch
                  your inbox at <span className="text-white">{email}</span>.
                </p>

                {/* Live activity strip — subtle, premium */}
                <div className="mt-5 space-y-2.5">
                  {[
                    { label: "Analyzing site signals", color: AMBER, delay: "0s" },
                    { label: "Mapping keyword universe", color: "#C77DFF", delay: "1.2s" },
                    { label: "Calibrating AI radar", color: PINK, delay: "2.4s" },
                  ].map((row) => (
                    <div
                      key={row.label}
                      className="flex items-center gap-3"
                      style={{
                        animation: `leadRowIn 520ms ease-out ${row.delay} both`,
                      }}
                    >
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{
                          background: row.color,
                          boxShadow: `0 0 8px ${row.color}`,
                          animation: "leadDotPulse 1.8s ease-in-out infinite",
                        }}
                      />
                      <span className="text-[12px] text-white/70 shrink-0">{row.label}</span>
                      <span
                        className="relative ml-1 h-[2px] flex-1 overflow-hidden rounded-full"
                        style={{ background: "rgba(255,255,255,0.06)" }}
                      >
                        <span
                          className="absolute inset-y-0 left-0 w-1/3 rounded-full"
                          style={{
                            background: `linear-gradient(90deg, transparent, ${row.color}, transparent)`,
                            animation: `leadBarSweep 2.4s ease-in-out ${row.delay} infinite`,
                          }}
                        />
                      </span>
                    </div>
                  ))}
                </div>

                <div
                  className="mt-5 rounded-[14px] px-4 py-3 text-center"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(255,138,61,0.12), rgba(255,93,143,0.10))",
                    border: `1px solid ${AMBER_SOFT}`,
                  }}
                >
                  <div className="text-[12.5px] text-white/75">
                    OrbitRank is now working on your business.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        body.orbit-lead-capture-open {
          overflow: hidden !important;
        }
        body.orbit-lead-capture-open .cw-bubble,
        body.orbit-lead-capture-open .cw-tooltip,
        body.orbit-lead-capture-open .cw-window {
          display: none !important;
        }
        .orbit-mobile-scroll {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .orbit-mobile-scroll::-webkit-scrollbar {
          display: none;
        }
        @keyframes leadModalIn {
          0% { opacity: 0; transform: translateY(18px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @media (max-width: 480px) {
          @keyframes leadModalIn {
            0% { opacity: 0; transform: translateY(10px); }
            100% { opacity: 1; transform: translateY(0); }
          }
        }
        @keyframes leadStageIn {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes leadSpin { to { transform: rotate(360deg); } }
        @keyframes leadChipPing {
          75%, 100% { transform: scale(2.4); opacity: 0; }
        }
        @keyframes leadRowIn {
          0% { opacity: 0; transform: translateX(-6px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes leadDotPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.35); opacity: 0.7; }
        }
        @keyframes leadBarSweep {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </>
  );
};

export default PostOnboardingLeadCapture;
